import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";

import { getGridIdName } from "~/features/profile/schemas/user-preferences-schema.ts";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useIsVendor } from "~/shared/hooks/use-is-vendor.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { putAndValidate } from "~/shared/services/api/fetch-client.ts";
import { pickEndpoint } from "~/shared/services/api/pick-endpoint.ts";
import {
	DEFAULT_ITEMS_PER_PAGE,
	VALID_ITEMS_PER_PAGE,
} from "~/shared/utils/default-grid-pager-settings.ts";
import { extractApiError } from "~/shared/utils/extract-api-error.ts";

import type {
	GridPreference,
	UserPreferences,
} from "../schemas/user-preferences-schema.ts";
import { userPreferencesSchema } from "../schemas/user-preferences-schema.ts";
import {
	getInternalUserPreferencesQueryKey,
	useGetInternalUserPreferences,
} from "./internal/use-get-internal-user-preferences.ts";
import { usePutInternalUserPreferences } from "./internal/use-put-internal-user-preferences.ts";
import {
	getUserPreferencesQueryKey,
	useGetUserPreferences,
} from "./use-get-user-preferences.ts";

const DEBOUNCE_MS = 500;

/** Snaps a value to a valid page size, falling back to the default. */
function normalizeItemsPerPage(value: number): number {
	return VALID_ITEMS_PER_PAGE.includes(
		value as (typeof VALID_ITEMS_PER_PAGE)[number],
	)
		? value
		: DEFAULT_ITEMS_PER_PAGE;
}

/** Reads the saved page size out of a grid's preference entry. */
function readItemsPerPage(gridPreference: GridPreference | undefined): number {
	if (gridPreference && "itemsPerPage" in gridPreference) {
		return normalizeItemsPerPage(gridPreference.itemsPerPage);
	}
	return DEFAULT_ITEMS_PER_PAGE;
}

/**
 * Makes a copy of every grid's saved preferences and changes just one grid's
 * page size. We copy (instead of editing in place) and send the whole map
 * because the API replaces the entire record on every save.
 */
function withUpdatedItemsPerPage(
	allGridPrefs: UserPreferences,
	gridId: number,
	newPageSize: number,
): UserPreferences {
	// Turn the numeric grid id (e.g. 8) into its API name (e.g. "ARDashboard...").
	const gridName = getGridIdName(gridId);
	// If we don't recognize the grid, change nothing and hand back what we got.
	if (!gridName) return allGridPrefs;

	// 1. Copy the whole map so we never mutate the original.
	const updatedPrefs = { ...allGridPrefs };

	// 2. Copy this one grid's existing settings, then overwrite its page size.
	//    (`columns ?? []` keeps any saved columns and satisfies the type when the
	//    grid has no entry yet.)
	const existingGridPrefs = updatedPrefs[gridId];
	updatedPrefs[gridId] = {
		...existingGridPrefs,
		gridId: gridName,
		columns: existingGridPrefs?.columns ?? [],
		itemsPerPage: newPageSize,
	};

	// 3. Hand back the updated copy.
	return updatedPrefs;
}

/**
 * A small "save this number, but a moment later" helper.
 *
 * How it works:
 * - `schedule(value)` waits 500ms after the LAST call, then saves once. Rapid
 *   changes (e.g. clicking through page sizes) collapse into a single save.
 * - If the user leaves before those 500ms are up — closing the tab, refreshing,
 *   or navigating away — the pending value is saved right away, with
 *   `keepalive` so the request can still finish while the page unloads.
 *
 * Returns `schedule` — call it every time the value changes.
 */
function useDebouncedSave(save: (value: number, keepalive: boolean) => void) {
	// `save` may be a brand-new function on every render, but the timer callback
	// below is created only once. Keep the newest `save` in a ref so the timer
	// always calls the latest version without having to be rebuilt.
	const latestSaveRef = useRef(save);
	useEffect(() => {
		latestSaveRef.current = save;
	}, [save]);

	// The running 500ms timer (if any) and the value that's waiting to be saved.
	const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
		undefined,
	);
	const pendingValueRef = useRef<number | undefined>(undefined);

	// Wait 500ms, then save. Calling again before then restarts the clock, so
	// only the final value is saved.
	const schedule = useCallback((value: number) => {
		pendingValueRef.current = value;
		if (debounceTimerRef.current !== undefined) {
			clearTimeout(debounceTimerRef.current);
		}
		debounceTimerRef.current = setTimeout(() => {
			debounceTimerRef.current = undefined;
			pendingValueRef.current = undefined;
			latestSaveRef.current(value, false);
		}, DEBOUNCE_MS);
	}, []);

	// Save the waiting value immediately instead of waiting out the 500ms. Used
	// when the page is going away. Does nothing if nothing is pending. (Only
	// touches refs, so it never needs to be rebuilt — hence the empty deps.)
	const saveNow = useCallback(() => {
		if (debounceTimerRef.current === undefined) return;
		clearTimeout(debounceTimerRef.current);
		debounceTimerRef.current = undefined;
		const value = pendingValueRef.current;
		pendingValueRef.current = undefined;
		if (value !== undefined) {
			latestSaveRef.current(value, true);
		}
	}, []);

	// Make sure a pending value is never lost when the page or component goes
	// away:
	// - `visibilitychange` (hidden) + `pagehide` cover tab close / refresh /
	//   backgrounding, which do NOT run React cleanup. (Recommended over
	//   `beforeunload`.)
	// - The cleanup `return` covers a normal unmount (SPA navigation / logout).
	useEffect(() => {
		const saveIfHidden = () => {
			if (document.visibilityState === "hidden") saveNow();
		};
		document.addEventListener("visibilitychange", saveIfHidden);
		globalThis.addEventListener("pagehide", saveNow);
		return () => {
			document.removeEventListener("visibilitychange", saveIfHidden);
			globalThis.removeEventListener("pagehide", saveNow);
			saveNow();
		};
	}, [saveNow]);

	return schedule;
}

type ItemsPerPageMutationParams = {
	gridId: number;
	allUserPreferences: UserPreferences | undefined;
	isInternal: boolean;
	isVendor: boolean;
	queryClient: ReturnType<typeof useQueryClient>;
};

const createItemsPerPageMutationConfig = (
	params: ItemsPerPageMutationParams,
) => ({
	mutationFn: async (newItemsPerPage: number) => {
		const { gridId, allUserPreferences, isInternal, isVendor } = params;

		const gridIdName = getGridIdName(gridId);
		if (!gridIdName) {
			throw new Error(`No grid name for gridId: ${gridId}`);
		}

		const endpoint = pickEndpoint(
			{
				internal: apiRoutes.INTERNALUSERPREFERENCES,
				vendor: apiRoutes.VENDORUSERPREFERENCES,
				external: apiRoutes.USERPREFERENCES,
			},
			{ isInternal, isVendor },
		);

		// API requires a FULL REPLACE: send the whole preferences map with only
		// this grid's itemsPerPage changed.
		const requestBody = withUpdatedItemsPerPage(
			allUserPreferences ?? {},
			gridId,
			newItemsPerPage,
		);

		const result = await putAndValidate(
			endpoint,
			requestBody,
			userPreferencesSchema,
		);

		if (!result.success) {
			const apiError = await extractApiError(result.error);
			throw new Error(apiError?.error ?? "Failed to update preferences");
		}

		return result.data as UserPreferences;
	},
	onSuccess: (data: UserPreferences) => {
		// Update the cache with the new data
		if (data) {
			params.queryClient.setQueryData(getUserPreferencesQueryKey(), data);
		}
	},
	onError: (error: Error) => {
		// Log error for debugging, but don't crash
		console.error("Failed to persist items per page preference:", error);
	},
});

export type UseItemsPerPagePreferenceReturn = {
	itemsPerPage: number;
	updateItemsPerPage: (value: number) => void;
	isLoading: boolean;
};

/**
 * Hook for reading and writing itemsPerPage preference for a specific grid.
 * Valid values: [25, 50, 75, 100]. Default/fallback: 25.
 *
 * Internal (TRAX) and external/vendor users persist to separate, dedicated
 * endpoints and query caches. This hook selects the correct read/write path
 * based on deployment type so every consumer (useGridState, useExternalPaging,
 * the quote board, the address book modal) gets internal persistence for free.
 *
 * - Reads current pref via useQuery
 * - updateItemsPerPage() validates value, optimistically updates cache, debounces 500ms PUT
 * - On unmount, flushes any pending debounced PUT
 *
 * @throws if gridId is not a positive integer
 */
export const useItemsPerPagePreference = (
	gridId: number,
): UseItemsPerPagePreferenceReturn => {
	if (!Number.isInteger(gridId) || gridId <= 0) {
		throw new Error(`Invalid gridId: ${gridId}. Must be a positive integer.`);
	}

	const { isInternal } = useDeploymentType();
	const { isVendor } = useIsVendor();
	const queryClient = useQueryClient();

	// External/vendor preferences (this query is disabled for internal users).
	const { data: externalAllPreferences } = useGetUserPreferences();
	const { data: externalGridPreference, isLoading: externalLoading } =
		useGetUserPreferences(gridId);

	// Internal (TRAX) preferences (this query is disabled for external/vendor
	// users). Internal preferences live in their own dedicated endpoint and
	// query cache, fully independent of the external/vendor flow.
	const {
		data: internalAllPreferences,
		isLoading: internalAllLoading,
		error: internalError,
	} = useGetInternalUserPreferences();
	const { data: internalGridPreference, isLoading: internalLoading } =
		useGetInternalUserPreferences(gridId);

	// Track whether the FULL internal preference map has successfully hydrated
	// from the server. We must never persist before this is true: a full-replace
	// PUT built from an empty/failed map would erase the user's other grid and
	// column preferences. Reset on error so a failed (re)fetch re-blocks writes
	// until a good map is loaded again.
	const hasHydratedRef = useRef(false);
	useEffect(() => {
		if (internalError) {
			hasHydratedRef.current = false;
			return;
		}
		if (!internalAllLoading && internalAllPreferences !== undefined) {
			hasHydratedRef.current = true;
		}
	}, [internalAllLoading, internalAllPreferences, internalError]);

	// Select the active read path based on deployment type.
	const gridPreference = isInternal
		? internalGridPreference
		: externalGridPreference;
	const isLoadingPreference = isInternal ? internalLoading : externalLoading;
	const activeQueryKey = isInternal
		? getInternalUserPreferencesQueryKey()
		: getUserPreferencesQueryKey();

	// Current saved page size for this grid (falls back to the default).
	const itemsPerPage = readItemsPerPage(gridPreference);

	// External/vendor mutation (only fired for non-internal users). The config
	// builds the full-replace body from `externalAllPreferences` and writes back
	// to the external query cache on success.
	const { mutate: persistExternalPreference } = useMutation(
		createItemsPerPageMutationConfig({
			gridId,
			allUserPreferences: externalAllPreferences,
			isInternal,
			isVendor,
			queryClient,
		}),
	);

	// Internal mutation (always persists to the internal endpoint).
	const { mutate: persistInternalPreference } = usePutInternalUserPreferences();

	// Saves the chosen page size. Internal and external users go to different
	// endpoints; `keepalive` is only meaningful for internal saves (it lets a
	// save started during page teardown still complete).
	const saveItemsPerPage = useCallback(
		(value: number, keepalive: boolean) => {
			if (!isInternal) {
				persistExternalPreference(value);
				return;
			}
			// Never persist before the full preference map has loaded — a
			// full-replace built from a partial/empty map would erase the user's
			// other grid and column preferences.
			if (!hasHydratedRef.current) return;
			// Build the body from the LATEST cache (not a stale snapshot) so a
			// near-simultaneous column-preference save isn't overwritten — the
			// column-sync writer shares this same cache.
			const currentPrefs = queryClient.getQueryData<UserPreferences>(
				getInternalUserPreferencesQueryKey(),
			);
			if (!currentPrefs) return;
			persistInternalPreference({
				preferences: withUpdatedItemsPerPage(currentPrefs, gridId, value),
				keepalive,
			});
		},
		[
			isInternal,
			gridId,
			queryClient,
			persistExternalPreference,
			persistInternalPreference,
		],
	);

	// Debounced, teardown-safe saving (see `useDebouncedSave`).
	const scheduleSave = useDebouncedSave(saveItemsPerPage);

	const updateItemsPerPage = useCallback(
		(value: number): void => {
			const safeValue = normalizeItemsPerPage(value);

			// Internal users: skip entirely until the saved map has loaded, so we
			// never send a full-replace that erases other prefs. The grid still
			// shows the new size via its own local paging state.
			if (isInternal && !hasHydratedRef.current) return;

			// Show the change immediately (optimistic cache update), then save it
			// after the debounce.
			queryClient.setQueryData<UserPreferences>(activeQueryKey, (old) =>
				withUpdatedItemsPerPage(old ?? {}, gridId, safeValue),
			);
			scheduleSave(safeValue);
		},
		[isInternal, gridId, queryClient, activeQueryKey, scheduleSave],
	);

	return {
		itemsPerPage,
		updateItemsPerPage,
		isLoading: isLoadingPreference,
	};
};
